// Copyright ©️ 2026 eVolve MEP, LLC

import type {
  BenderProfile,
  BendTypeName,
  BendTypeWithYBCRows,
  ConduitSizeId,
  ConduitTypeId,
  WithRequiredNotNull,
  YBCRow,
} from '@evolvemep/api-ts';
import type { FormErrors } from '@mantine/form';

import type { WithNullable } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongEntries, strongFromEntries } from 'helpers/strongEntries';
import type { BendCreatorFormValues } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/Context/useBendCreatorFormContext';

const isCompleteYbcRow = (row: WithNullable<YBCRow>): row is YBCRow =>
  isNotNil(row.straightLength) && isNotNil(row.rotation) && isNotNil(row.angle);

/** @remarks returns null when a Custom bend's rows are incomplete or too few to send. */
export const getBendTypeWithYBCRows = ({
  bendType,
  ybcRows,
}: WithRequiredNotNull<
  Pick<BendCreatorFormValues, 'bendType' | 'ybcRows'>,
  'bendType'
>): BendTypeWithYBCRows | null => {
  if (bendType !== 'Custom') return { bendType };
  const rows = ybcRows.map(({ straightLength, rotation, angle }) => ({ straightLength, rotation, angle }));
  if (!rows.every(isCompleteYbcRow) || rows.length < 2) return null;
  return { bendType, ybcRows: rows };
};

export const withoutYbcRowErrors = (errors: FormErrors): FormErrors =>
  strongFromEntries(strongEntries(errors).filter(([path]) => !path.startsWith('ybcRows')));

export const extractConduitTypes = ({ configurationValues }: BenderProfile): ConduitTypeId[] => [
  ...new Set(configurationValues.map((config) => config.conduitType.conduitTypeId)),
];
export const extractConduitSizes = (
  { configurationValues }: BenderProfile,
  conduitTypeId: ConduitTypeId,
): ConduitSizeId[] => [
  ...new Set(
    configurationValues
      .filter((config) => config.conduitType.conduitTypeId === conduitTypeId)
      .map((config) => config.conduitSize.conduitSizeId),
  ),
];

/** A blank part name becomes the new bend type; a name holding the previous bend type has it swapped for the new one. */
export const getSuggestedPartName = (
  partName: string,
  previousBendType: BendTypeName | null,
  bendType: BendTypeName,
) => {
  if (partName.trim() === '') return bendType;
  if (isNil(previousBendType)) return partName;
  return partName.replace(previousBendType, bendType);
};
